import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Eye, EyeOff, Loader2 } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [slowHint, setSlowHint] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        setSlowHint(false);
        // Se o login demorar mais de 4s, mostra hint "A acordar o servidor..."
        const slowTimer = setTimeout(() => setSlowHint(true), 4000);
        try {
            const r = await login(email, password);
            if (!r.ok) {
                setError(r.error || "Falha no login");
            } else {
                const dest = r.user?.role === "gestor" ? "/leads-parceiros" : "/";
                navigate(dest, { replace: true });
            }
        } finally {
            clearTimeout(slowTimer);
            setSlowHint(false);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left – Branding */}
            <div className="hidden lg:flex flex-col justify-between bg-zinc-900 text-white p-12 bg-grain relative overflow-hidden">
                <div className="relative z-10">
                    <div className="overline text-zinc-400">Salvador • MEO</div>
                    <div className="font-display text-5xl font-black tracking-tighter mt-2">
                        Controlo de
                        <br />
                        Vendas.
                    </div>
                </div>
                <div className="relative z-10 space-y-6">
                    <div className="grid grid-cols-2 gap-4 max-w-md">
                        <div className="border border-zinc-700 p-4 rounded-md">
                            <div className="overline text-zinc-500">
                                Lucro Mês
                            </div>
                            <div className="font-display text-2xl font-bold mt-1 text-emerald-400">
                                +428€
                            </div>
                        </div>
                        <div className="border border-zinc-700 p-4 rounded-md">
                            <div className="overline text-zinc-500">
                                Meta atingida
                            </div>
                            <div className="font-display text-2xl font-bold mt-1">
                                72%
                            </div>
                        </div>
                    </div>
                    <p className="text-zinc-400 max-w-sm text-sm leading-relaxed">
                        Regista vendas, acompanha instalações e segue o teu
                        objetivo mensal num único painel.
                    </p>
                </div>
                {/* Decorative */}
                <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-[#002FE6] opacity-30 blur-3xl" />
            </div>

            {/* Right – Form */}
            <div className="flex items-center justify-center p-6 lg:p-12 bg-[#f9f9f9]">
                <form
                    onSubmit={submit}
                    className="w-full max-w-md space-y-6"
                    data-testid="login-form"
                >
                    <div>
                        <div className="overline">Iniciar sessão</div>
                        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">
                            Bem-vindo de volta.
                        </h1>
                        <p className="text-zinc-600 mt-2">
                            Introduz as tuas credenciais para aceder ao painel.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="overline block mb-2">
                                Email
                            </label>
                            <input
                                data-testid="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="meo.salvador@gmail.com"
                                required
                                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FE6] focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="overline block mb-2">
                                Palavra-passe
                            </label>
                            <div className="relative">
                                <input
                                    data-testid="login-password"
                                    type={show ? "text" : "password"}
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    placeholder="••••"
                                    required
                                    className="w-full px-4 py-3 pr-12 bg-white border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FE6] focus:border-transparent"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow(!show)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                                >
                                    {show ? (
                                        <EyeOff size={18} />
                                    ) : (
                                        <Eye size={18} />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div
                            data-testid="login-error"
                            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        data-testid="login-submit"
                        type="submit"
                        disabled={loading}
                        className="w-full bg-zinc-900 hover:bg-[#002FE6] disabled:opacity-60 text-white font-semibold py-3.5 rounded-md transition-colors flex items-center justify-center gap-2"
                    >
                        {loading && <Loader2 size={16} className="animate-spin" />}
                        {loading ? "A entrar..." : "Entrar"}
                    </button>
                    {slowHint && (
                        <div
                            data-testid="login-slow-hint"
                            className="text-xs text-zinc-500 text-center bg-zinc-50 border border-zinc-200 rounded px-3 py-2"
                        >
                            ⏳ A acordar o servidor… pode demorar uns segundos a primeira vez. Não atualizes a página.
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
